import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { theme } from '../../../theme/tokens';
interface MetricChartProps {
  title: string;
  tooltipText?: string;
  currentValue: string;
  growthPercent: number;
  previousValue: string;
  data: any[];
  previousData?: any[];
  dataKey: string;
  xAxisKey?: string;
  yAxisFormatter?: (value: any) => string;
  tooltipValueFormatter?: (value: any) => string;
  tooltipLabel?: string;
  dateFormatter: (date: string) => string;
  updateTime: string;
  chartColor?: string;
}
export function MetricChart({
  title,
  tooltipText,
  currentValue,
  growthPercent,
  previousValue,
  data,
  previousData,
  dataKey,
  xAxisKey = 'date',
  yAxisFormatter,
  tooltipValueFormatter,
  tooltipLabel = '',
  dateFormatter,
  updateTime,
  chartColor = theme.brand.primary
}: MetricChartProps) {
  const {
    t
  } = useTranslation('analytics');
  // CRITICAL: Ensure we have valid data
  if (!data || data.length === 0) {
    console.error(`❌ ${title} - No data available`);
    return null;
  }
  // Get the actual first and last dates from the data
  const firstDate = data[0]?.[xAxisKey];
  const lastDate = data[data.length - 1]?.[xAxisKey];
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1977">
      <div className="flex items-start justify-between mb-4" data-id="element-1978">
        <div className="flex-1" data-id="element-1979">
          <div className="flex items-center gap-2 mb-2" data-id="element-1980">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-1981">{title}</h3>
            {tooltipText && <button className="text-gray-400 hover:text-gray-600 group relative" data-id="element-1982">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" data-id="element-1983">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" data-id="element-1984" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" data-id="element-1985" />
                </svg>
                <div className="hidden group-hover:block absolute left-0 top-6 w-72 p-3 bg-gray-900 text-white text-xs text-left rounded shadow-lg z-10" data-id="element-1986">
                  {tooltipText}
                </div>
              </button>}
          </div>
          <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-1987">
            {currentValue}
            <span className={`text-sm ml-2 ${growthPercent >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1988">
              {growthPercent >= 0 ? '+' : ''}
              {growthPercent.toFixed(2)}%
            </span>
          </div>
          <div className="text-xs text-gray-500" data-id="element-1989">
            {previousValue} {t('components.MetricChart.previous_period')}
          </div>
        </div>
      </div>
      <div className="h-48 relative" role="img" aria-label={`${title} chart. Current value ${currentValue}, ${growthPercent >= 0 ? 'up' : 'down'} ${Math.abs(growthPercent).toFixed(2)} percent versus previous period (${previousValue}).`} data-id="element-1990">
        {/* Custom X-axis labels - Stripe-style alignment */}
        <div className="absolute bottom-0 left-0 right-0 flex pb-1" style={{
        paddingLeft: '40px'
      }} data-id="element-1991">
          <span className="text-xs text-gray-600" data-id="element-1992">
            {dateFormatter(firstDate)}
          </span>
          <span className="text-xs text-gray-600 ml-auto" data-id="element-1993">
            {dateFormatter(lastDate)}
          </span>
        </div>

        <ResponsiveContainer width="100%" height="100%" data-id="element-1994">
          <LineChart data={data} margin={{
          left: 0,
          right: 0,
          top: 5,
          bottom: 20
        }} data-id="element-1995">
            <CartesianGrid strokeDasharray="0" vertical={false} stroke="#d1d5db" strokeWidth={1} data-id="element-1996" />

            <XAxis dataKey={xAxisKey} tick={false} axisLine={false} tickLine={false} height={0} data-id="element-1997" />

            <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} width={40} tick={{
            fill: '#666',
            fontSize: 12,
            fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
          }} tickCount={5} data-id="element-1998" />

            <Tooltip content={({
            active,
            payload
          }) => {
            if (active && payload && payload.length) {
              const currentData = payload.find(p => !p.strokeDasharray);
              const previousData = payload.find(p => p.strokeDasharray);
              return <div className="bg-white p-3 border border-gray-200 rounded shadow-lg" data-id="element-2000">
                      <div className="text-sm font-medium mb-2" data-id="element-2001">{title}</div>
                      {currentData && <div className="text-sm mb-1" data-id="element-2002">
                          <span className="text-gray-600" data-id="element-2003">
                            {dateFormatter(currentData.payload[xAxisKey])}
                          </span>
                          <div className="font-semibold text-gray-900" data-id="element-2004">
                            {tooltipValueFormatter ? tooltipValueFormatter(currentData.value) : currentData.value}
                            {tooltipLabel && ` ${tooltipLabel}`}
                          </div>
                        </div>}
                      {previousData && <div className="text-sm" data-id="element-2005">
                          <span className="text-gray-500" data-id="element-2006">
                            {dateFormatter(previousData.payload[xAxisKey])}{' '}
                            {t('components.MetricChart.prev')}
                          </span>
                          <div className="font-medium text-gray-600" data-id="element-2007">
                            {tooltipValueFormatter ? tooltipValueFormatter(previousData.value) : previousData.value}
                            {tooltipLabel && ` ${tooltipLabel}`}
                          </div>
                        </div>}
                    </div>;
            }
            return null;
          }} data-id="element-1999" />

            <Line type="monotone" dataKey={dataKey} stroke={chartColor} strokeWidth={2} dot={false} data-id="element-2008" />

            {previousData && previousData.length > 0 && <Line type="monotone" dataKey={dataKey} stroke={chartColor} strokeWidth={1.5} strokeDasharray="5 5" dot={false} data={previousData} opacity={0.5} data-id="element-2009" />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-xs text-gray-500 mt-2" data-id="element-2010">{updateTime}</div>
    </div>;
}